import React, { useState } from "react";
import { db } from "../services/firebase";
import {
  collection,
  query,
  where,
  getDoc,
  doc,
  getDocs,
} from "firebase/firestore";
import "./Login.css";
import { useNavigate } from "react-router-dom";
import bcrypt from "bcryptjs";
import { useBuyer } from "../contexts/BuyerContext";
import { saveData } from "../utils/storage";
import Logo from "../JMIlogo.png";

const Login = () => {
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const { updateBuyer } = useBuyer();

  // Login with mobile and password (no OTP)
  const handleLogin = async () => {
    if (loading) return;

    if (mobile.length !== 10) {
      alert("Enter a valid 10-digit mobile number");
      return;
    }

    if (!password) {
      alert("Please enter your password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Find buyer by mobile number
      const q = query(collection(db, "buyers"), where("mobile", "==", mobile));
      const snap = await getDocs(q);

      if (snap.empty) {
        alert("Buyer not found with this mobile number");
        setLoading(false);
        return;
      }

      const buyerDoc = snap.docs[0];
      const data = buyerDoc.data();

      // Verify password
      const isMatch = await bcrypt.compare(password, data.passwordHash);
      if (!isMatch) {
        alert("Incorrect password");
        setLoading(false);
        return;
      }

      const buyerId = data.buyerId;

      // Fetch BuyerSegmentRegistrations to get { segment: operatorId }
      const buyerSegmentRef = doc(db, "BuyerSegmentRegistrations", buyerId);
      const buyerSegmentSnap = await getDoc(buyerSegmentRef);
      const operatorsBySegment = buyerSegmentSnap.exists()
        ? buyerSegmentSnap.data().segments || {}
        : {};

      // Fetch sellersBySegment
      const sellersBySegment = {};
      for (const [segment, operatorId] of Object.entries(operatorsBySegment)) {
        if (operatorId) {
          try {
            const operatorSegmentRef = doc(
              db,
              "OperatorSegmentRegistrations",
              operatorId,
            );
            const operatorSegmentSnap = await getDoc(operatorSegmentRef);
            sellersBySegment[segment] = operatorSegmentSnap.exists()
              ? operatorSegmentSnap.data().sellerIds || []
              : [];
          } catch (err) {
            console.error(
              `Failed to fetch sellers for operator ${operatorId}`,
              err,
            );
            sellersBySegment[segment] = [];
          }
        }
      }

      const buyerRegistrationRef = doc(db, "buyerregistrations", buyerId);
      const buyerRegistrationSnap = await getDoc(buyerRegistrationRef);
      const buyerRegistrationData = buyerRegistrationSnap.exists()
        ? buyerRegistrationSnap.data()
        : {};

      const organizationName = buyerRegistrationData.organizationName || null;
      const shippingLatitude = buyerRegistrationData.shippingLatitude || null;
      const shippingLongitude = buyerRegistrationData.shippingLongitude || null;
      const storeLogoUrl = buyerRegistrationData.storeLogoUrl || null;
      const privatePasskey = buyerRegistrationData.privatePasskey || [];
      const streetAddress = buyerRegistrationData.streetAddress || null;
      const transactionalMobile =
        buyerRegistrationData.transactionalMobile || null;
      const state = buyerRegistrationData.contactState || null;
      const district = buyerRegistrationData.contactDistrict || null;

      // Update context and storage
      updateBuyer({
        buyerId,
        mobile,
        registrationStatus: true,
        lastUpdated: Date.now(),
        operatorsBySegment,
        sellersBySegment,
        organizationName,
        shippingLatitude,
        shippingLongitude,
        logoUrl: storeLogoUrl,
        privatePasskey,
        streetAddress,
        transactionalMobile,
        state,
        district,
      });

      // Save to storage
      await saveData("buyerData", {
        buyerId,
        mobile,
        registrationStatus: true,
        lastUpdated: Date.now(),
        operatorsBySegment,
        sellersBySegment,
        organizationName,
        shippingLatitude,
        shippingLongitude,
        logoUrl: storeLogoUrl,
        privatePasskey,
        streetAddress,
        transactionalMobile,
        state,
        district,
      });

      // Fetch profile for redirect
      const buyerProfileRef = doc(db, "profile", buyerId);
      const profileSnap = await getDoc(buyerProfileRef);
      const profileData = profileSnap.exists() ? profileSnap.data() : {};

      alert("Login successful");

      const redirectPath = !profileData.BuyerRegistration
        ? "/buyerregistration"
        : "/dashboard";

      nav(redirectPath, { replace: true });
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || "Login failed");
      alert("Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrapper">
      <h1 className="title">
        <img src={Logo} className="logo" alt="logo" />
      </h1>

      <div className="login-box">
        <h2> Login</h2>

        <label>Mobile Number</label>
        <input
          type="tel"
          maxLength="10"
          placeholder="Enter Mobile Number"
          value={mobile}
          onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
        />

        <label>Password</label>
        <input
          type="password"
          placeholder="Enter your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <button className="login-btn" onClick={handleLogin} disabled={loading}>
          {loading ? "Logging in..." : "Login"}
        </button>

        <p className="forgot" onClick={() => nav("/forgetpassword")}>
          Forgot Password?
        </p>
      </div>

      {error && <p className="error">{error}</p>}

      <p className="register-link">
        Don't have an account?{" "}
        <span onClick={() => nav("/register")}>Register now</span>
      </p>

      <p className="support">
        <img src="/whatsapp.svg" alt="whatsapp" className="whatsapp-icon" />
        <span
          onClick={() => window.open("https://wa.me/918917412728", "_blank")}
        >
          {" "}
          Need help?{" "}
        </span>
        Contact Support
      </p>
    </div>
  );
};

export default Login;
